import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  IdentificationIcon,
  CalendarIcon,
  KeyIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { LoadingSpinner, EmptyState, AccessibleButton, FormInput, Modal } from '../../components/ui';

const AgentProfilePage = () => {
  const { user } = useAuth();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/field-agent/auth/profile');
      setAgent(response.data.agent);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('שגיאה בטעינת הפרופיל');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const validatePassword = () => {
    const errors = {};

    if (!passwordData.current_password) {
      errors.current_password = 'נא להזין סיסמה נוכחית';
    }

    if (!passwordData.new_password) {
      errors.new_password = 'נא להזין סיסמה חדשה';
    } else if (passwordData.new_password.length < 8) {
      errors.new_password = 'הסיסמה חייבת להיות לפחות 8 תווים';
    }

    if (!passwordData.confirm_password) {
      errors.confirm_password = 'נא לאמת את הסיסמה החדשה';
    } else if (passwordData.new_password !== passwordData.confirm_password) {
      errors.confirm_password = 'הסיסמאות אינן תואמות';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!validatePassword()) {
      toast.error('נא לתקן את השגיאות בטופס');
      return;
    }

    try {
      setChangingPassword(true);
      await axios.put('/api/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });

      toast.success('הסיסמה שונתה בהצלחה');
      closePasswordModal();
    } catch (error) {
      console.error('Error changing password:', error);
      const errorMessage = error.response?.data?.message || 'שגיאה בשינוי סיסמה';
      toast.error(errorMessage);
      if (error.response?.data?.field) {
        setPasswordErrors(prev => ({
          ...prev,
          [error.response.data.field]: errorMessage
        }));
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const closePasswordModal = () => {
    if (changingPassword) return;
    setShowPasswordModal(false);
    setPasswordData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setPasswordErrors({});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" color="primary" text="טוען פרופיל..." />
      </div>
    );
  }

  if (!agent && !user) {
    return (
      <div className="bg-white shadow-lg rounded-xl border border-gray-100">
        <EmptyState
          type="error"
          title="שגיאה בטעינת הפרופיל"
          description="לא ניתן לטעון את פרטי הפרופיל"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="text-center sm:text-right">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">הפרופיל שלי</h1>
        <p className="mt-2 text-sm text-gray-600">
          פרטים אישיים ומידע על הסוכן
        </p>
      </div>

      {/* Profile Card */}
      <div className="bg-white shadow-lg rounded-xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-br from-primary-500 via-primary-600 to-blue-600 px-6 py-8 sm:px-8 sm:py-10">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
            <div className="flex-shrink-0">
              <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-full bg-white flex items-center justify-center shadow-xl ring-4 ring-white/20">
                <span className="text-3xl sm:text-4xl font-bold text-primary-600">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
            </div>
            <div className="text-center sm:text-right sm:mr-6 flex-1">
              <h2 className="text-2xl sm:text-3xl font-bold text-white">
                {user?.first_name} {user?.last_name}
              </h2>
              <p className="text-primary-100 mt-2 text-base">סוכן מכירות</p>
              {agent?.status === 'active' && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                  <CheckCircleIcon className="h-4 w-4 text-white" />
                  <span className="text-sm font-medium text-white">פעיל</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-6 sm:px-8 sm:py-8">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {/* Email */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <EnvelopeIcon className="h-5 w-5 text-white" />
                </div>
                <p className="text-sm font-semibold text-blue-900">אימייל</p>
              </div>
              <a 
                href={`mailto:${user?.email}`}
                className="text-base font-medium text-gray-900 hover:text-primary-600 transition-colors break-all"
              >
                {user?.email || '-'}
              </a>
            </div>

            {/* Phone */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-500 rounded-lg">
                  <PhoneIcon className="h-5 w-5 text-white" />
                </div>
                <p className="text-sm font-semibold text-green-900">טלפון</p>
              </div>
              {agent?.phone || user?.phone ? (
                <a 
                  href={`tel:${agent?.phone || user?.phone}`}
                  className="text-base font-medium text-gray-900 hover:text-primary-600 transition-colors"
                >
                  {agent?.phone || user?.phone}
                </a>
              ) : (
                <p className="text-base font-medium text-gray-500">-</p>
              )}
            </div>

            {/* Employee ID */}
            {agent?.employee_id && (
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-purple-500 rounded-lg">
                    <IdentificationIcon className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-purple-900">מספר עובד</p>
                </div>
                <p className="text-base font-medium text-gray-900">{agent.employee_id}</p>
              </div>
            )}

            {/* Region */}
            {agent?.region && (
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-orange-500 rounded-lg">
                    <MapPinIcon className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-orange-900">אזור</p>
                </div>
                <p className="text-base font-medium text-gray-900">{agent.region}</p>
              </div>
            )}

            {/* Hire Date */}
            {agent?.hire_date && (
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border border-indigo-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-indigo-500 rounded-lg">
                    <CalendarIcon className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-indigo-900">תאריך תחילת עבודה</p>
                </div>
                <p className="text-base font-medium text-gray-900">{formatDate(agent.hire_date)}</p>
              </div>
            )}

            {/* Commission Rate */}
            {agent?.commission_rate && (
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-4 border border-teal-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-teal-500 rounded-lg">
                    <CurrencyDollarIcon className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-teal-900">אחוז עמלה</p>
                </div>
                <p className="text-base font-medium text-gray-900">{agent.commission_rate}%</p>
              </div>
            )}
          </div>

          {/* Manager Info */}
          {agent?.manager?.user && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5 text-gray-500" />
                מנהל ישיר
              </h3>
              <div className="flex items-center gap-4 bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary-500 to-blue-600 flex items-center justify-center shadow-md">
                    <span className="text-base font-bold text-white">
                      {agent.manager.user.first_name?.[0]}{agent.manager.user.last_name?.[0]}
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-gray-900">
                    {agent.manager.user.first_name} {agent.manager.user.last_name}
                  </p>
                  <a 
                    href={`mailto:${agent.manager.user.email}`}
                    className="text-sm text-gray-600 hover:text-primary-600 transition-colors break-all"
                  >
                    {agent.manager.user.email}
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {agent?.notes && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">הערות</h3>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <p className="text-sm text-gray-900 leading-relaxed">{agent.notes}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Security Card */}
      <div className="bg-white shadow-lg rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary-100 rounded-lg">
            <ShieldCheckIcon className="h-6 w-6 text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">אבטחה</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          עדכן את הסיסמה שלך באופן קבוע כדי לשמור על אבטחת החשבון שלך
        </p>
        <AccessibleButton
          onClick={() => setShowPasswordModal(true)}
          variant="primary"
          size="md"
          icon={KeyIcon}
          iconPosition="left"
          ariaLabel="שנה סיסמה"
        >
          שנה סיסמה
        </AccessibleButton>
      </div>

      {/* Password Change Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={closePasswordModal}
        title="שינוי סיסמה"
        size="md"
        preventClose={changingPassword}
        ariaLabel="שינוי סיסמה"
      >
        <form onSubmit={handleChangePassword} className="space-y-5">
          <p className="text-sm text-gray-600 mb-4">
            הזן את הסיסמה הנוכחית והסיסמה החדשה הרצויה
          </p>

          <FormInput
            label="סיסמה נוכחית"
            type="password"
            name="current_password"
            value={passwordData.current_password}
            onChange={(e) => {
              setPasswordData(prev => ({ ...prev, current_password: e.target.value }));
              if (passwordErrors.current_password) {
                setPasswordErrors(prev => ({ ...prev, current_password: '' }));
              }
            }}
            error={passwordErrors.current_password}
            required
            showPasswordToggle
          />

          <FormInput
            label="סיסמה חדשה"
            type="password"
            name="new_password"
            value={passwordData.new_password}
            onChange={(e) => {
              setPasswordData(prev => ({ ...prev, new_password: e.target.value }));
              if (passwordErrors.new_password) {
                setPasswordErrors(prev => ({ ...prev, new_password: '' }));
              }
              // Clear confirm password error if passwords match
              if (passwordData.confirm_password && e.target.value === passwordData.confirm_password) {
                setPasswordErrors(prev => ({ ...prev, confirm_password: '' }));
              }
            }}
            error={passwordErrors.new_password}
            required
            minLength={8}
            showPasswordToggle
            helpText="לפחות 8 תווים"
          />

          <FormInput
            label="אימות סיסמה חדשה"
            type="password"
            name="confirm_password"
            value={passwordData.confirm_password}
            onChange={(e) => {
              setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }));
              if (passwordErrors.confirm_password) {
                setPasswordErrors(prev => ({ ...prev, confirm_password: '' }));
              }
              // Real-time validation
              if (passwordData.new_password && e.target.value !== passwordData.new_password) {
                setPasswordErrors(prev => ({ ...prev, confirm_password: 'הסיסמאות אינן תואמות' }));
              } else if (passwordData.new_password && e.target.value === passwordData.new_password) {
                setPasswordErrors(prev => ({ ...prev, confirm_password: '' }));
              }
            }}
            error={passwordErrors.confirm_password}
            required
            showPasswordToggle
          />

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <AccessibleButton
              type="button"
              onClick={closePasswordModal}
              disabled={changingPassword}
              variant="ghost"
              size="md"
              ariaLabel="ביטול שינוי סיסמה"
            >
              ביטול
            </AccessibleButton>
            <AccessibleButton
              type="submit"
              disabled={changingPassword}
              loading={changingPassword}
              variant="primary"
              size="md"
              ariaLabel="שמור סיסמה חדשה"
            >
              שמור
            </AccessibleButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AgentProfilePage;

